import { useState } from "react";
import {
  knowledgeEyebrow,
  knowledgeTitle,
  knowledgeLead,
  knowledgeResourceLinks,
  knowledgeAboutParagraph,
  knowledgeCatalogueCards,
  knowledgeSections,
  knowledgeClinicalSources,
  knowledgeClinicalSourcesIntro,
  knowledgeBusinessInfo,
  knowledgeDisclaimer,
} from "./knowledgeHubContent";

export default function KnowledgeHub() {
  const [query, setQuery] = useState("");

  const totalQuestions = knowledgeSections.reduce(
    (sum, section) => sum + section.questions.length,
    0
  );

  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  const filteredSections = isSearching
    ? knowledgeSections
        .map((section) => ({
          ...section,
          questions: section.questions.filter(
            (item) =>
              item.question.toLowerCase().includes(trimmedQuery) ||
              item.answer.toLowerCase().includes(trimmedQuery)
          ),
        }))
        .filter((section) => section.questions.length > 0)
    : [];

  const matchCount = filteredSections.reduce(
    (sum, section) => sum + section.questions.length,
    0
  );

  return (
    <section
      id="knowledge-hub"
      aria-labelledby="hb-knowledge-title"
      className="section-padding bg-background-100 border-t border-background-200"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <p className="font-semibold text-xs tracking-widest uppercase mb-3 font-sans text-accent-600">
          {knowledgeEyebrow}
        </p>
        <h2
          id="hb-knowledge-title"
          className="font-heading text-3xl md:text-4xl font-bold text-primary-800 mb-4"
        >
          {knowledgeTitle}
        </h2>
        <p className="text-foreground-600 text-sm leading-relaxed max-w-2xl">
          {knowledgeLead}
        </p>

        {/* Resource links */}
        <nav
          aria-label="Healing and Breakthrough resources"
          className="flex flex-wrap gap-x-6 gap-y-2 mt-6"
        >
          {knowledgeResourceLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary-600 hover:text-primary-800 underline underline-offset-4 transition-colors cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Expandable catalogue + Q&A */}
        <details className="group/cat mt-8 rounded-2xl border border-background-200 bg-background-50 overflow-hidden">
          <summary className="flex items-center justify-between gap-4 px-5 md:px-8 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden hover:bg-background-100 transition-colors">
            <span className="font-heading text-base md:text-lg font-semibold text-primary-800 leading-snug pr-4">
              Explore the business catalogue &amp; {totalQuestions} questions and answers
            </span>
            <span className="w-9 h-9 flex items-center justify-center rounded-full shrink-0 bg-primary-100 text-primary-700 group-open/cat:rotate-45 transition-transform duration-300">
              <i className="ri-add-line text-lg" />
            </span>
          </summary>

          <div className="px-5 md:px-8 pb-10 pt-4">
            <h3 className="font-heading text-xl font-semibold text-primary-800 mt-4 mb-3">
              About Healing &amp; Breakthrough Coaching
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
              {knowledgeAboutParagraph}
            </p>

            {/* Catalogue cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              {knowledgeCatalogueCards.map((card) => (
                <div
                  key={card.title}
                  className="rounded-xl border border-background-200 bg-background-100 p-6"
                >
                  <h4 className="font-heading text-base font-semibold text-primary-800 mb-3">
                    {card.title}
                  </h4>

                  {card.facts && (
                    <dl className="space-y-3">
                      {card.facts.map((fact) => (
                        <div key={fact.label}>
                          <dt className="text-xs font-semibold uppercase tracking-wide text-accent-700 mb-0.5">
                            {fact.label}
                          </dt>
                          <dd className="text-sm text-foreground-600 leading-relaxed">
                            {fact.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  {card.paragraphs?.map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-sm text-foreground-600 leading-relaxed mb-3 last:mb-0"
                    >
                      {paragraph}
                    </p>
                  ))}

                  {card.list && (
                    <ul className="space-y-2 mb-3">
                      {card.list.map((item) => (
                        <li
                          key={item}
                          className="flex gap-2 text-sm text-foreground-600 leading-relaxed"
                        >
                          <i className="ri-check-line text-accent-600 mt-0.5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {card.footnote && (
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {card.footnote.prefix}
                      <a
                        href={card.footnote.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 underline underline-offset-2 hover:text-primary-800 transition-colors"
                      >
                        {card.footnote.linkLabel}
                      </a>
                      {card.footnote.suffix}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Search */}
            <div className="mt-10">
              <label htmlFor="hub-search" className="sr-only">
                Search the questions
              </label>
              <div className="relative max-w-lg">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none text-foreground-500">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  id="hub-search"
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search the questions&hellip;"
                  autoComplete="off"
                  className="w-full pl-11 pr-11 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                />
                {isSearching && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                )}
              </div>
              {isSearching && (
                <p
                  className="text-sm text-foreground-600 mt-3"
                  aria-live="polite"
                >
                  {matchCount > 0
                    ? `${matchCount} matching ${
                        matchCount === 1 ? "question" : "questions"
                      } found`
                    : "No matching questions. Try a different keyword."}
                </p>
              )}
            </div>

            {/* Topic navigation */}
            {!isSearching && (
              <>
                <h3 className="font-heading text-xl font-semibold text-primary-800 mt-10 mb-3">
                  Explore the knowledge topics
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl mb-4">
                  These areas connect Federica&rsquo;s coaching approach with the questions
                  people ask about everyday life, energy and wellbeing.
                </p>
                <nav
                  aria-label="Knowledge topics"
                  className="flex flex-wrap gap-x-6 gap-y-2 mb-8"
                >
                  {knowledgeSections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="text-sm font-medium text-primary-600 hover:text-primary-800 underline underline-offset-4 transition-colors cursor-pointer"
                    >
                      {section.navLabel}
                    </a>
                  ))}
                </nav>
              </>
            )}

            {/* Question sections */}
            {isSearching
              ? filteredSections.map((section) => (
                  <section key={section.id} className="mt-8">
                    <h3 className="font-heading text-lg font-semibold text-primary-800 mb-2">
                      {section.heading}
                    </h3>
                    <div className="space-y-3">
                      {section.questions.map((item) => (
                        <div
                          key={item.id}
                          className="rounded-xl border border-background-200 bg-background-100 p-5"
                        >
                          <h4 className="text-sm font-semibold text-foreground-800 leading-snug mb-2">
                            {item.question}
                          </h4>
                          <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
                            {item.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>
                ))
              : knowledgeSections.map((section) => (
                  <section
                    key={section.id}
                    id={section.id}
                    aria-labelledby={`${section.id}-heading`}
                    className="scroll-mt-24 mt-8"
                  >
                    <h3
                      id={`${section.id}-heading`}
                      className="font-heading text-lg font-semibold text-primary-800 mb-2"
                    >
                      {section.heading}
                    </h3>
                    <div className="border-t border-background-200">
                      {section.questions.map((item) => (
                        <details
                          key={item.id}
                          id={item.id}
                          className="group/qa border-b border-background-200 scroll-mt-24"
                        >
                          <summary className="flex items-center justify-between gap-4 py-4 pr-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden hover:text-primary-700 transition-colors">
                            <span className="text-sm font-semibold text-foreground-800 leading-snug">
                              {item.question}
                            </span>
                            <i className="ri-add-line text-foreground-500 shrink-0 transition-transform duration-300 group-open/qa:rotate-45" />
                          </summary>
                          <div className="pb-4 pr-6">
                            <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
                              {item.answer}
                            </p>
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>
                ))}

            {/* Clinical sources */}
            <section
              aria-labelledby="hb-clinical-sources"
              className="mt-10 rounded-xl border border-background-200 bg-background-100 p-6"
            >
              <h4
                id="hb-clinical-sources"
                className="font-heading text-base font-semibold text-primary-800 mb-2"
              >
                Further clinical information
              </h4>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {knowledgeClinicalSourcesIntro}
              </p>
              <ul className="space-y-2">
                {knowledgeClinicalSources.map((source) => (
                  <li key={source.href} className="flex gap-2 text-sm leading-relaxed">
                    <i className="ri-external-link-line text-accent-600 mt-0.5 shrink-0" />
                    <a
                      href={source.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 underline underline-offset-2 transition-colors"
                    >
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            {/* Boundary note */}
            <div className="mt-6 rounded-xl border-l-4 border-accent-500 bg-accent-50 p-5">
              <p className="text-xs text-foreground-600 leading-relaxed">
                {knowledgeDisclaimer}
              </p>
            </div>

            <p className="text-xs text-foreground-500 leading-relaxed mt-4">
              {knowledgeBusinessInfo}
            </p>
          </div>
        </details>
      </div>
    </section>
  );
}